/**
 * ThemeToggle — yorug'/qorong'u rejim almashtirgich
 *
 * Spec bo'lim 20: 650ms silliq aylana animatsiya (circle-reveal-liquid).
 * Bosilgan nuqtadan boshlab radial ravishda yangi tema "ochiladi".
 * Reduced motion holatida animatsiya darhol amalga oshadi (a11y).
 *
 * Theme localStorage'da saqlanadi va html class orqali qo'llanadi.
 */

import { Moon, Sun } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { cn } from '@/shared/lib/cn';

type Theme = 'dark' | 'light';

const STORAGE_KEY = 'avtofix-theme';

function getInitialTheme(): Theme {
  // Default = 'light' (klassik professional ERP uslubi). Dark — ixtiyoriy (toggle).
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.remove('dark', 'light');
  root.classList.add(theme);
  localStorage.setItem(STORAGE_KEY, theme);
}

export function ThemeToggle(): React.ReactElement {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const nextTheme: Theme = theme === 'dark' ? 'light' : 'dark';

      // Reduced motion — darhol o'tkazish
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hasViewTransition = 'startViewTransition' in document;

      if (prefersReduced || !hasViewTransition) {
        setTheme(nextTheme);
        return;
      }

      // Circle reveal animatsiya (650ms, spec bo'lim 20)
      const x = e.clientX;
      const y = e.clientY;
      const maxRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y),
      );

      setIsAnimating(true);

      const transition = document.startViewTransition(() => {
        setTheme(nextTheme);
      });

      transition.ready
        .then(() => {
          document.documentElement.animate(
            {
              clipPath: [
                `circle(0px at ${String(x)}px ${String(y)}px)`,
                `circle(${String(maxRadius)}px at ${String(x)}px ${String(y)}px)`,
              ],
            },
            {
              duration: 650,
              easing: 'ease-in-out',
              pseudoElement: '::view-transition-new(root)',
            },
          );
        })
        .catch(() => {
          // Fallback — animatsiyasiz
        });

      transition.finished
        .then(() => {
          setIsAnimating(false);
        })
        .catch(() => {
          setIsAnimating(false);
        });
    },
    [theme],
  );

  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={isAnimating}
      className={cn(
        'flex size-9 items-center justify-center rounded-lg border border-border',
        'text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        'disabled:pointer-events-none',
      )}
      aria-label={isDark ? "Yorug' rejimga o'tkazish" : "Qorong'u rejimga o'tkazish"}
      title={isDark ? "Yorug' rejim" : "Qorong'u rejim"}
    >
      {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  );
}
